"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { axiosDoctorProfile } from "../../../../api/axios";
import "./DoctorClinicInfoPanel.css";

const safe = (value) => (value == null ? "" : String(value).trim());
const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const getRawDoctor = (doctor) => doctor?.raw || {};

const uniqueStrings = (items) =>
  Array.from(new Set(items.map((item) => safe(item)).filter(Boolean)));

const normalizeTextList = (items) =>
  uniqueStrings(
    asArray(items).map((item) =>
      typeof item === "string"
        ? item
        : item?.name || item?.label || item?.language || item?.title || item?.value
    )
  );

const normalizeCertifications = (doctor, doctorInfo, t) => {
  const rawDoctor = getRawDoctor(doctor);
  const source =
    asArray(doctorInfo?.certifications).length
      ? doctorInfo.certifications
      : asArray(doctor?.certifications).length
      ? doctor?.certifications
      : rawDoctor?.certifications || rawDoctor?.diplomas;

  return asArray(source)
    .map((item) => {
      if (typeof item === "string") {
        return { id: item, title: item };
      }

      const title =
        safe(item?.title) ||
        safe(item?.name) ||
        safe(item?.label) ||
        safe(item?.certificationName) ||
        t("doctorClinicInfo.certification");

      return {
        id: item?.id || item?._id || title,
        title,
      };
    })
    .filter((item) => safe(item?.title));
};

const resolvePracticeLabel = (doctor, t) => {
  const rawDoctor = getRawDoctor(doctor);

  return (
    safe(doctor?.practicePlace) ||
    safe(rawDoctor?.practicePlace) ||
    t("doctorClinicInfo.privatePractice")
  );
};

const resolveAddressText = (doctor, t) => {
  const rawDoctor = getRawDoctor(doctor);
  const clinicAddress = doctor?.clinic?.address || rawDoctor?.clinic?.address;
  const parts = [
    safe(clinicAddress?.street),
    safe(clinicAddress?.city),
    safe(clinicAddress?.country),
  ].filter(Boolean);

  return (
    parts.join(", ") ||
    safe(doctor?.address) ||
    safe(doctor?.clinicAddress) ||
    safe(rawDoctor?.address) ||
    safe(rawDoctor?.clinicAddress) ||
    [safe(doctor?.city) || safe(rawDoctor?.city), safe(doctor?.country) || safe(rawDoctor?.country)]
      .filter(Boolean)
      .join(", ") ||
    t("doctorClinicInfo.addressNotAvailable")
  );
};

const resolveClinicQuery = (doctor, addressText) => {
  const rawDoctor = getRawDoctor(doctor);

  return (
    safe(addressText) ||
    safe(doctor?.location?.address) ||
    safe(rawDoctor?.location?.address) ||
    safe(doctor?.practicePlace) ||
    "clinic"
  );
};

const resolveLanguages = (doctor, doctorInfo) => {
  const rawDoctor = getRawDoctor(doctor);

  return normalizeTextList(
    asArray(doctorInfo?.languages).length
      ? doctorInfo.languages
      : asArray(doctor?.languages).length
      ? doctor?.languages
      : doctor?.spokenLanguages || rawDoctor?.languages || rawDoctor?.spokenLanguages
  );
};

const resolveInsurances = (doctor, doctorInfo) => {
  const rawDoctor = getRawDoctor(doctor);
  const hasDoctorInfoInsurances = Array.isArray(doctorInfo?.insurances);
  const source = hasDoctorInfoInsurances
    ? doctorInfo.insurances
    : doctor?.acceptedInsurance ||
      doctor?.insuranceCoverage ||
      doctor?.insurances ||
      rawDoctor?.acceptedInsurance ||
      rawDoctor?.insuranceCoverage ||
      rawDoctor?.insurances;

  return normalizeTextList(source);
};

const buildEmbedMapUrl = (query) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(query)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;

export default function DoctorClinicInfoPanel({ doctor }) {
  const { t } = useTranslation();
  const [doctorInfo, setDoctorInfo] = useState(null);

  useEffect(() => {
    const doctorId = doctor?.id || doctor?._id || doctor?.doctorId;
    if (!doctorId) {
      setDoctorInfo(null);
      return undefined;
    }

    let active = true;
    axiosDoctorProfile
      .get(`/doctor-info/by-doctor/${encodeURIComponent(String(doctorId))}`)
      .then(({ data }) => active && setDoctorInfo(data || null))
      .catch(() => active && setDoctorInfo(null));

    return () => {
      active = false;
    };
  }, [doctor]);

  const clinicName = useMemo(
    () => safe(doctor?.clinicName) || safe(doctor?.clinic?.name) || t("doctorClinicInfo.defaultClinicName"),
    [doctor, t]
  );
  const practiceLabel = useMemo(() => resolvePracticeLabel(doctor, t), [doctor, t]);
  const addressText = useMemo(() => resolveAddressText(doctor, t), [doctor, t]);
  const languages = useMemo(() => resolveLanguages(doctor, doctorInfo), [doctor, doctorInfo]);
  const certifications = useMemo(
    () => normalizeCertifications(doctor, doctorInfo, t),
    [doctor, doctorInfo, t]
  );
  const insurances = useMemo(() => resolveInsurances(doctor, doctorInfo), [doctor, doctorInfo]);
  const mapQuery = useMemo(() => resolveClinicQuery(doctor, addressText), [doctor, addressText]);
  const mapUrl = useMemo(() => buildEmbedMapUrl(mapQuery), [mapQuery]);
  const navigateUrl = useMemo(
    () => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`,
    [mapQuery]
  );

  if (!doctor) return null;

  return (
    <div className="dcip-wrap">
      <section className="dcip-card" aria-label={t("doctorClinicInfo.panelTitle")}>
        <div className="dcip-head">
          <h2 className="dcip-panelTitle">{t("doctorClinicInfo.panelTitle")}</h2>
        </div>

        <div className="dcip-section dcip-sectionClinic">
          <div className="dcip-clinicText">
            <h3 className="dcip-title">{t("doctorClinicInfo.title")}</h3>

            <div className="dcip-detailRow">
              <span className="dcip-detailIcon" aria-hidden>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 21C12 21 19 15.4 19 10A7 7 0 1 0 5 10C5 15.4 12 21 12 21Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </span>

              <div className="dcip-detailContent">
                <div className="dcip-practiceLabel">{practiceLabel}</div>
                <div className="dcip-clinicName">{clinicName}</div>
                <div className="dcip-address">{addressText}</div>
              </div>
            </div>

            <div className="dcip-detailRow dcip-detailRowInsurance">
              <span className="dcip-detailIcon dcip-detailIconSuccess" aria-hidden>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3L18 5.75V11C18 15.25 15.3 18.88 12 20C8.7 18.88 6 15.25 6 11V5.75L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9.5 11.75L11.25 13.5L14.75 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>

              <div className="dcip-detailContent dcip-detailContentInsurance">
                <div className="dcip-subtitle">{t("doctorClinicInfo.insuranceCoverage")}</div>
                {insurances.length ? (
                  <div className="dcip-insuranceList">
                    {insurances.map((insurance) => (
                      <div className="dcip-insuranceBadge" key={insurance}>
                        <span className="dcip-insuranceName">{insurance}</span>
                        <span className="dcip-insuranceStatus">{t("doctorClinicInfo.acceptedInsurance")}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="dcip-insuranceEmpty">
                    {t("doctorClinicInfo.noAcceptedInsurance", {
                      defaultValue: "Aucune assurance conventionnee.",
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="dcip-mapCard">
            <div className="dcip-map">
              <iframe src={mapUrl} loading="lazy" title={t("doctorClinicInfo.mapTitle")} allowFullScreen />
            </div>
            <a className="dcip-navigate" href={navigateUrl} target="_blank" rel="noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 11.5L20.5 3L12 20.5L10 13.5L3 11.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{t("doctorClinicInfo.navigate")}</span>
            </a>
          </div>
        </div>

        <div className="dcip-section dcip-inlineSection">
          <h3 className="dcip-title dcip-titleInline">{t("doctorClinicInfo.certifications")}</h3>
          <div className="dcip-chipList">
            {certifications.length ? (
              
              certifications.map((item) => (
              <span key={item.id} className="dcip-chipTag">
                <span>{item.title}</span>
              </span>
            ))
            ) : (
              <span className="dcip-empty">—</span>
            )}
          </div>
        </div>

        <div className="dcip-section dcip-inlineSection">
          <h3 className="dcip-title dcip-titleInline">{t("doctorClinicInfo.spokenLanguage")}</h3>
          <div className="dcip-chipList">
            {languages.length ? (
             
            languages.map((language) => (
              <span key={language} className="dcip-chipTag">
                <span>{language}</span>
              </span>
            ))
            ) : (
              <span className="dcip-empty">—</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}